import clsx from 'clsx';
import { ChangeEvent, DragEvent, useEffect, useState } from 'react';
import { FaPlus, FaUpload } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';

import { addImage, getImageByProject, removeImage } from '../../Store/imageQueueSlice';
import { RootState } from '../../Store/store';
import HookFormImageUpload from './HookFormImageUpload';
import ImageTypeSelection from './ImageTypeSelection';
import useImageUploadService from './useImageUploadService';

interface MultipleImageUploadProps {
  projectId: string;
}

const MultipleImageUpload = ({ projectId }: MultipleImageUploadProps) => {
  const dispatch = useDispatch();
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [showTypeSelection, setShowTypeSelection] = useState<boolean>(true);
  const [uploadSettings, setUploadSettings] = useState<{ isTma: boolean; estCoreDiameterInMm?: number } | null>(null);
  const imageQueue = useSelector((state: RootState) => getImageByProject(state, projectId));
  const emptyQueue = imageQueue.queue.length + imageQueue.inprog.length + imageQueue.done.length === 0;
  const { uploadImage } = useImageUploadService();

  // Offer a 24h session whenever more than one upload is unfinished — whether
  // added together or one at a time. Clear the choice once the queue drains;
  // logout clears it too via localStorage.clear() on signOut. AuthenticatedUser
  // reads the flag. null flag = not yet decided this run, so prompt once.
  const activeCount = imageQueue.queue.length + imageQueue.inprog.length;
  const [askStayLoggedIn, setAskStayLoggedIn] = useState(false);
  useEffect(() => {
    if (activeCount === 0) {
      localStorage.removeItem('stayLoggedIn24h');
      setAskStayLoggedIn(false);
    } else if (activeCount > 1 && localStorage.getItem('stayLoggedIn24h') === null) {
      setAskStayLoggedIn(true);
    }
  }, [activeCount]);

  const answerStayLoggedIn = (keep: boolean) => {
    localStorage.setItem('stayLoggedIn24h', keep ? 'true' : 'false');
    setAskStayLoggedIn(false);
  };

  const addImageToQueue = (imgs: FileList | null) =>
    Array.from(imgs ?? []).forEach((img) => {
      const imageData = {
        projectId: projectId,
        imageId: Date.now() + Math.random(),
        name: img.name,
        blobUrl: URL.createObjectURL(img),
        size: img.size,
        type: img.type,
        isTma: uploadSettings?.isTma,
        estCoreDiameterInMm: uploadSettings?.estCoreDiameterInMm,
      };
      dispatch(addImage(imageData));
      uploadImage(imageData, {
        isTma: uploadSettings?.isTma ?? false,
        estCoreDiameterInMm: uploadSettings?.estCoreDiameterInMm
      });
    });

  const handleDrag = (e: DragEvent<HTMLFormElement | HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    addImageToQueue(e.dataTransfer.files);
    setDragActive(false);
    e.dataTransfer.clearData();
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    addImageToQueue(e.target.files);
  };

  const removeDone = () => {
    dispatch(removeImage({ status: "done" }));
  };

  const handleTypeSelect = (type: 'whole-slide' | 'tma', coreSize?: number) => {
    setUploadSettings({
      isTma: type === 'tma',
      estCoreDiameterInMm: coreSize
    });
    setShowTypeSelection(false);
  };

  const uploadScreen = (
    <label
      htmlFor="dropzone-file"
      className={clsx(
        "relative block w-full rounded-lg border-2 border-dashed p-12 text-center transition-all duration-200",
        "border-gray-300 hover:border-blue-500 focus-within:border-blue-500",
        "bg-white dark:bg-gray-800",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        { "border-blue-500 ring-2 ring-blue-500 ring-offset-2": dragActive },
      )}
    >
      <FaUpload className="mx-auto h-12 w-12 text-gray-500 dark:text-gray-400" />
      <span className="mt-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
        Click to upload or drag and drop
      </span>
      <div 
        className="absolute inset-0 cursor-pointer" 
        onDragEnter={handleDrag} 
        onDragLeave={handleDrag} 
        onDragOver={handleDrag} 
        onDrop={handleDrop} 
      />
    </label>
  );

  const fileQueue = (
    <ul role="list" className="max-h-full divide-y divide-gray-200 dark:divide-gray-700 overflow-y-scroll">
      <li className="relative pb-1">
        <div className="mx-auto flex justify-between items-center p-2">
          <label 
            htmlFor="dropzone-file" 
            className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Upload more
          </label>
          <button 
            type="button" 
            onClick={() => removeDone()}
            className="rounded-md bg-gray-200 dark:bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
          >
            Clear Completed
          </button>
        </div>
      </li>
      {imageQueue.done.map((img, idx) => (
        <HookFormImageUpload key={idx} id={img.imageId} status="done" />
      ))}
      {imageQueue.inprog.map((img, idx) => (
        <HookFormImageUpload key={idx} id={img.imageId} status="inprog" />
      ))}
      {imageQueue.queue.map((img, idx) => (
        <HookFormImageUpload key={idx} id={img.imageId} status="queue" />
      ))}
    </ul>
  );

  if (showTypeSelection) {
    return <ImageTypeSelection onSelect={handleTypeSelect} />;
  }

  return (
    <div 
      className="container mx-auto relative"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input multiple onChange={handleChange} id="dropzone-file" type="file" className="hidden" />
      {emptyQueue ? uploadScreen : fileQueue}
      
      {/* Drop overlay that appears when dragging files */}
      {dragActive && (
        <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-80 dark:bg-opacity-80 backdrop-blur-sm border-2 border-blue-500 border-dashed rounded-lg flex items-center justify-center">
          <div className="text-center bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90 p-6 rounded-lg shadow-lg">
            <FaUpload className="mx-auto h-12 w-12 text-blue-500" />
            <span className="mt-2 block text-sm font-semibold text-blue-700 dark:text-blue-300">
              Drop files to add to upload queue
            </span>
          </div>
        </div>
      )}

      {askStayLoggedIn && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999]" aria-hidden="true" />
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="stay-logged-in-title"
          >
            <div className="modal-box max-w-sm w-full p-6 flex flex-col gap-4">
              <h3 id="stay-logged-in-title" className="text-lg font-bold">
                Keep me logged in?
              </h3>
              <p className="text-sm text-base-content/70">
                {activeCount} uploads are in progress and may take a while. Stay logged in for 24 hours so they aren't interrupted?
              </p>
              <div className="flex gap-3 justify-end">
                <button className="btn btn-ghost btn-sm" onClick={() => answerStayLoggedIn(false)}>
                  No
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => answerStayLoggedIn(true)}>
                  Stay logged in
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MultipleImageUpload;
